'use client';

import { WindowAdapter } from 'builder/WindowAdapter';
import { useRouter } from 'next/navigation';
import {
    createContext,
    JSX,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

// Next.js registers its own popstate listener in a passive effect (app-router.js). React flushes all
// layout effects before any passive effect for a commit, so registering our listener in a layout
// effect attaches it before Next's, and DOM listeners fire in registration order — which is what lets
// stopImmediatePropagation() here actually prevent Next from traversing a blocked back/forward.
// useLayoutEffect warns during SSR, so fall back to useEffect on the server, where neither runs any
// DOM logic anyway.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Next.js patches window.history.pushState/replaceState inside a passive effect (after hydration) so
// that external calls dispatch its internal ACTION_RESTORE. Capturing the native implementation here,
// at module top level, runs at import time — before React commits, and therefore before Next patches
// it. Using this native reference to correct the address bar after blocking a back/forward keeps the
// correction invisible to Next's router bookkeeping instead of triggering a spurious restore.
const nativeHistoryPushState: typeof window.history.pushState =
    typeof window !== 'undefined' ? window.history.pushState.bind(window.history) : () => {};

function isModifiedClick(event: MouseEvent): boolean {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

/**
 * Returns the guardable in-app href for a click target, or null if the click should be ignored:
 * no anchor, opens in a new tab/window, is a download, or points somewhere we never guard
 * (external, protocol-relative, or hash-only).
 */
function findGuardableHref(target: EventTarget | null): string | null {
    if (!(target instanceof Element)) {
        return null;
    }
    const link = target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) {
        return null;
    }
    if (link.target && link.target !== '_self') {
        return null;
    }
    if (link.hasAttribute('download')) {
        return null;
    }
    const href = link.getAttribute('href');
    if (
        !href ||
        href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('//') ||
        href.startsWith('#')
    ) {
        return null;
    }
    return href;
}

export interface NavigationGuardConfig {
    /** Whether guarding is active at all (e.g. there is unsaved work). */
    enabled: boolean;
    /** Returns true if navigating to `to` should be guarded (blocked pending confirmation). */
    shouldGuard: (to: string) => boolean;
    /** The URL to restore in the address bar if a guarded back/forward navigation is blocked. */
    currentUrl: string;
}

export interface NavigationGuard {
    active: boolean;
    accept: () => void;
    reject: () => void;
}

interface PendingNavigation {
    /** Performs the navigation the user was trying to make, once they confirm. */
    proceed: () => void;
}

interface NavigationGuardContextValue {
    setGuardConfig: (config: NavigationGuardConfig | null) => void;
    active: boolean;
    accept: () => void;
    reject: () => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null);

/**
 * Guards in-app navigation. Intercepts browser back/forward (popstate) with a synchronous history
 * correction so rapid, repeated back presses can't race past an unresolved confirmation.
 *
 * Must be mounted at the app root (see app/providers.tsx): the popstate listener must attach before
 * Next.js's own App Router popstate listener so that stopping propagation here prevents Next from
 * reacting to a blocked event.
 *
 * Scope: only in-app navigation is guarded (link clicks, router.push/replace, and same-document
 * back/forward). A true page unload — refresh, tab close, typed URL, or a back/forward the browser
 * serves as a full document load or bfcache restore — cannot be intercepted with a custom modal; the
 * only browser API for that is a native (uncustomizable) beforeunload dialog, which we intentionally
 * do not use. Those navigations proceed without a confirmation.
 */
export function NavigationGuardProvider({ children }: PropsWithChildren): JSX.Element {
    const configRef = useRef<NavigationGuardConfig | null>(null);
    // The App Router history state (Next's __NA marker + route tree) of the entry the guarded page is
    // sitting on. Snapshotted when the guard config is set, and reused when correcting the address bar
    // after a blocked back/forward — see setGuardConfig and handlePopState below.
    const currentHistoryStateRef = useRef<unknown>(null);
    const pendingRef = useRef<PendingNavigation | null>(null);
    const [pending, setPending] = useState<PendingNavigation | null>(null);

    const router = useRouter();
    const routerRef = useRef(router);
    useEffect(() => {
        routerRef.current = router;
    }, [router]);

    const activate = useCallback((next: PendingNavigation): void => {
        pendingRef.current = next;
        setPending(next);
    }, []);

    useIsomorphicLayoutEffect(() => {
        function handlePopState(event: PopStateEvent): void {
            const to = window.location.pathname + window.location.search;
            const config = configRef.current;
            if (!config?.enabled || !config.shouldGuard(to)) {
                return;
            }
            event.stopImmediatePropagation();
            // Reuse the entry's real Next.js state instead of null: a null-state entry can't be routed
            // by Next's popstate handler (it early-returns on `!event.state`), which would later leave
            // the address bar showing this URL while the page stays on the previous route.
            nativeHistoryPushState(currentHistoryStateRef.current, '', config.currentUrl);
            activate({ proceed: () => WindowAdapter.setLocation(to) });
        }
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [activate]);

    useEffect(() => {
        function handleClick(event: MouseEvent): void {
            if (event.defaultPrevented || isModifiedClick(event)) {
                return;
            }
            const href = findGuardableHref(event.target);
            if (href === null) {
                return;
            }
            const config = configRef.current;
            if (!config?.enabled || !config.shouldGuard(href)) {
                return;
            }
            // Prevent both the browser's default navigation and Next's own <Link> handler (attached
            // in the bubble phase) — this capture-phase listener runs first.
            event.preventDefault();
            event.stopImmediatePropagation();
            // router.push is programmatic (not a DOM click), so it does not re-enter this listener.
            activate({ proceed: () => routerRef.current.push(href) });
        }
        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [activate]);

    const setGuardConfig = useCallback((config: NavigationGuardConfig | null): void => {
        configRef.current = config;
        if (config) {
            // Runs from the consumer's effect after each navigation commit, so window.history.state is
            // the Next.js state for the page currently mounting the guard (config.currentUrl).
            currentHistoryStateRef.current = window.history.state;
        }
    }, []);

    const accept = useCallback((): void => {
        const next = pendingRef.current;
        pendingRef.current = null;
        setPending(null);
        next?.proceed();
    }, []);

    const reject = useCallback((): void => {
        pendingRef.current = null;
        setPending(null);
    }, []);

    const contextValue = useMemo<NavigationGuardContextValue>(
        () => ({ setGuardConfig, active: pending !== null, accept, reject }),
        [setGuardConfig, pending, accept, reject],
    );

    return <NavigationGuardContext.Provider value={contextValue}>{children}</NavigationGuardContext.Provider>;
}

export function useNavigationGuard({ enabled, shouldGuard, currentUrl }: NavigationGuardConfig): NavigationGuard {
    const context = useContext(NavigationGuardContext);
    if (!context) {
        throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
    }

    const { setGuardConfig, active, accept, reject } = context;

    useEffect(() => {
        setGuardConfig({ enabled, shouldGuard, currentUrl });
        return () => setGuardConfig(null);
    }, [setGuardConfig, enabled, shouldGuard, currentUrl]);

    return { active, accept, reject };
}
